/**
 * Created By Henal Khianra
 * Created at 25/03/2020
 * https://lashesorlando.com/pdf/11.pdf
 */

import React from 'react';
import { Document, Font, Page, pdf, StyleSheet, Text, View } from '@react-pdf/renderer';
import moment from 'moment';
import filter from 'ramda/src/filter';
import isNil from 'ramda/src/isNil';
import Icon from '@components/@core/Icon';
import PdfHeader from '@components/PdfHeader';
import { CompanyPerformanceReportMenu } from '~/services/config';

const styles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        paddingLeft: 20,
        paddingRight: 20,
        paddingBottom: 20,
        alignItems: 'stretch',
    },
    listHeader: {
        display: 'flex',
        flexDirection: 'row',
        paddingBottom: '10px',
        paddingTop: '10px',
        borderBottomWidth: 1.3,
        borderBottomColor: 'gray',
        borderBottomStyle: 'solid',
    },
    listItem: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        marginLeft: 10,
        marginRight: 10,
        paddingTop: 10,
        paddingBottom: 10,
        borderBottomWidth: 1.3,
        borderBottomColor: 'gray',
        borderBottomStyle: 'solid',
        minHeight: 25,
        maxHeight: 100,
    },
    pageNumbers: {
        position: 'absolute',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        bottom: 10,
        left: 0,
        right: 0,
        textAlign: 'center',
        margin: '10px',
        fontSize: 10,
        fontFamily: 'Roboto',
        fontWeight: 'normal',
    },
    titleHead: { fontSize: 14, fontWeight: 'bold', fontFamily: 'Roboto' },
    listTitle: { fontSize: 10, fontWeight: 'normal', fontFamily: 'Roboto' },
    listItemFont: { fontSize: 8, fontWeight: 'light', fontFamily: 'Roboto' },
    listItemData: { fontSize: 11, fontWeight: 'light', fontFamily: 'Roboto' },
});

Font.register({
    family: 'Roboto',
    fonts: [
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'normal',
        },
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'bold',
        },
        {
            src: 'https://fonts.gstatic.com/s/oswald/v13/Y_TKV6o8WovbUd3m_X9aAA.ttf',
            fontWeight: 'light',
        },
    ],
});

const columns = {
    employees: [
        { name: 'Employee', flex: 2 },
        { name: 'ScoreCard', flex: 2 },
        { name: 'Score', flex: 1 },
        { name: 'Commitments', flex: 1 },
        { name: 'Employee', flex: 1 },
    ],
    chart: [
        { name: 'Department', flex: 2 },
        { name: 'Teams', flex: 1 },
        { name: 'Employees', flex: 1 },
        { name: 'Completed', flex: 1 },
        { name: 'Score', flex: 1 },
    ],
};

export const CompanyPerformancePdf = (props) => {
    const { date, data, activeTab } = props;
    const dateStart = moment(date.start).format('MMM DD');
    const dateEnd = moment(date.end).format('MMM DD');
    const pageTitle = 'Reports - Performance';

    const view = {
        employees: (item, index) => (
            <View style={styles.listItem} key={`crd${index}`} wrap break={!((index + 1) % 13)}>
                <View style={{ flex: 2 }}>
                    <Text style={styles.listItemData}>{`${item.user.profile.first_name} ${item.user.profile.last_name}`}</Text>
                </View>
                <View style={{ flex: 2 }}>
                    <Text style={styles.listItemData}>{0 == item.scorecards.length ? 'No Scorecard assigned' : item.scorecards[0].title}</Text>
                </View>
                <View style={{ flex: 1 }}>
                    {isNil(item.performance) ? (
                        <Text style={styles.listItemData}>Incomplete</Text>
                    ) : (
                        <Text>
                            {item.performance.status == 2 ? (
                                <Text>
                                    {item.performance.data?.average_star_rating !== 'N/A' ? (
                                        <Text style={styles.listItemData}>{item.performance.data.average_star_rating}</Text>
                                    ) : (
                                        <Text style={styles.listItemData}>Na</Text>
                                    )}
                                </Text>
                            ) : (
                                <Text style={styles.listItemData}>Incomplete</Text>
                            )}
                        </Text>
                    )}
                </View>
                <View style={{ flex: 1 }}>
                    {isNil(item.performance) ? (
                        <Text style={styles.listItemData}>Incomplete</Text>
                    ) : (
                        <View>
                            <View>
                                <Text style={styles.listItemData}>Learning: {item.performance.trainings.length}</Text>
                            </View>
                            <View>
                                <Text style={styles.listItemData}>Task: {item.performance.tasks.length}</Text>
                            </View>
                        </View>
                    )}
                </View>
                <View style={{ flex: 1 }}>{item.performance?.status === 2 && <Text style={styles.listItemData}>{moment(item.performance.completed_at).format('MM/DD/YY')}</Text>}</View>
            </View>
        ),
        chart: (item, index) => (
            <View style={styles.listItem} key={`crd${index}`} wrap break={!((index + 1) % 13)}>
                <View style={{ flex: 2 }}>
                    <Text style={styles.listItemData}>{item.department?.name || item.department}</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.listItemData}>0</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.listItemData}>{item.totals?.employees?.count || 0}</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.listItemData}>{item.totals?.reviews_completed?.count || 0}</Text>
                </View>
                <View style={{ flex: 1 }}>
                    <Text style={styles.listItemData}>{item.totals?.completed_average_star_score?.stars || 0}</Text>
                </View>
            </View>
        ),
    };

    const rows = activeTab === 'employees' ? data.users : data.departments;

    pdf(
        <Document>
            <Page size="letter" style={styles.container} wrap>
                <PdfHeader header={pageTitle} style={{ paddingTop: 10, paddingBottom: 10 }} date={`${dateStart} - ${dateEnd}`} fixed />
                <View style={{ marginLeft: 10, marginRight: 10 }} fixed>
                    <View style={{ paddingBottom: 10 }}>
                        <View style={{ flex: 1 }}>
                            <Text style={styles.titleHead}>Performance Report</Text>
                        </View>
                    </View>
                    <View style={styles.listHeader}>
                        {columns[activeTab].map(({ name, flex }, index) => (
                            <View key={`col${index}`} style={{ flex }}>
                                <Text style={styles.listTitle}>{name}</Text>
                            </View>
                        ))}
                    </View>
                </View>
                {rows.map(view[activeTab])}
                <Text fixed style={styles.pageNumbers} render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`} />
            </Page>
        </Document>
    )
        .toBlob()
        .then((e) => {
            const url = URL.createObjectURL(e);
            window.open(url, '__blank');
        });
};

const PdfIcon = ({ state, props, name }) => {
    const { data } = props;
    const { dateStart, dateEnd, activeTab } = state;
    const date = moment.range(dateStart, dateEnd);

    const handlePdf = () => {
        CompanyPerformancePdf({ date, data, activeTab });
    };

    if (name) {
        handlePdf();
        return null;
    }

    return <Icon name="fas fa-print" color="#343f4b" size={16} onClick={handlePdf} />;
};

export default PdfIcon;
